import { useCallback } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutGrid, LineChart, Building2, ArrowLeftRight, Users, Inbox, LogOut, ExternalLink, ShieldCheck, ScrollText, Quote } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/site/Logo";
import useIdleLogout from "@/hooks/useIdleLogout";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutGrid, end: true, id: "overview" },
  { to: "/admin/stocks", label: "Stocks", icon: LineChart, id: "stocks" },
  { to: "/admin/properties", label: "Properties", icon: Building2, id: "properties" },
  { to: "/admin/requests", label: "Deposits & withdrawals", icon: ArrowLeftRight, id: "requests" },
  { to: "/admin/verification", label: "Identity checks", icon: ShieldCheck, id: "verification" },
  { to: "/admin/clients", label: "Clients", icon: Users, id: "clients" },
  { to: "/admin/enquiries", label: "Enquiries", icon: Inbox, id: "enquiries" },
  { to: "/admin/testimonials", label: "Testimonials", icon: Quote, id: "testimonials" },
  { to: "/admin/audit", label: "Audit log", icon: ScrollText, id: "audit" },
];

const cls = ({ isActive }) =>
  `flex shrink-0 items-center gap-3 rounded-xl px-4 py-2.5 text-sm transition-colors duration-200 ${isActive ? "bg-emerald-500/10 text-emerald-400" : "text-slate-400 hover:bg-white/5 hover:text-white"}`;

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const onIdle = useCallback(async () => {
    await logout();
    navigate("/admin/login", { state: { error: "You were signed out after 15 minutes of inactivity." } });
  }, [logout, navigate]);
  useIdleLogout(onIdle);
  return (
    <div className="min-h-screen bg-obsidian text-slate-100 lg:flex" data-testid="admin-layout">
      <aside className="border-b border-slate-800 bg-surface/50 lg:sticky lg:top-0 lg:h-screen lg:w-72 lg:shrink-0 lg:border-b-0 lg:border-r">
        <div className="flex items-center justify-between p-6"><Logo to="/admin" testId="admin-logo-link" /></div>
        <nav className="no-scrollbar flex gap-1 overflow-x-auto px-4 pb-4 lg:flex-col lg:overflow-visible">
          {NAV.map(({ to, label, icon: Icon, end, id }) => (
            <NavLink key={to} to={to} end={end} className={cls} data-testid={`admin-nav-${id}`}><Icon className="h-4 w-4" />{label}</NavLink>
          ))}
        </nav>
        <div className="hidden px-6 lg:absolute lg:bottom-6 lg:block lg:w-72">
          <div className="flex items-center gap-3 border-t border-slate-800 pt-6">
            {user.picture ? <img src={user.picture} alt="" className="h-9 w-9 rounded-full" /> : <div className="h-9 w-9 rounded-full bg-emerald-500/20" />}
            <div className="min-w-0">
              <p className="truncate text-sm text-white" data-testid="admin-user-name">{user.name}</p>
              <p className="truncate text-xs text-slate-500">{user.email}</p>
            </div>
          </div>
        </div>
      </aside>
      <div className="flex-1">
        <header className="sticky top-0 z-40 flex items-center justify-end gap-4 border-b border-slate-800 bg-obsidian/80 px-6 py-4 backdrop-blur-xl lg:px-10">
          <Link to="/" className="inline-flex min-h-10 items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="admin-view-site-link"><ExternalLink className="h-4 w-4" /> View site</Link>
          <button onClick={async () => { await logout(); navigate("/admin/login"); }} className="inline-flex min-h-10 items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="admin-logout-button">
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </header>
        <main className="px-4 py-10 sm:px-6 lg:px-10"><Outlet /></main>
      </div>
    </div>
  );
}
